import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

/**
 * Character + animation asset catalog.
 *
 * Files live in packages/client/public/ and are served from the root path:
 *   /characters/<id>/<id>.gltf
 *   /animations/<id>/<id>.gltf
 *
 * All files use the standard Mixamo bone naming (`mixamorig:*`), so any
 * animation clip plays correctly on any character.
 */

export const CHARACTERS = [
  "char_clowny",
  "char_elvir",
  "char_oldguy",
  "char_rastaman",
  "char_wrestler",
  "char_zombie",
] as const;
export type CharacterId = (typeof CHARACTERS)[number];

export const ANIMATIONS = [
  "idle",
  "jumping",
  "running_forwards",
  "running_backwards",
  "change_direction",
  "kick_1_martelo",
  "kick_2_mma",
  "kick_3_knee",
  "kick_4_mma",
  "punch_1_jab",
] as const;
export type AnimationId = (typeof ANIMATIONS)[number];

const loader = new GLTFLoader();

// Cache the LOADED gltf for each character. We clone the scene per-player.
const charCache = new Map<CharacterId, Promise<GLTF>>();
// Cache the AnimationClip itself. Clips are immutable + shareable across mixers.
const animCache = new Map<AnimationId, Promise<THREE.AnimationClip>>();

export function loadCharacter(id: CharacterId): Promise<GLTF> {
  const cached = charCache.get(id);
  if (cached) return cached;
  const p = loader.loadAsync(`/characters/${id}/${id}.gltf`);
  charCache.set(id, p);
  return p;
}

export function loadAnimation(id: AnimationId): Promise<THREE.AnimationClip> {
  const cached = animCache.get(id);
  if (cached) return cached;
  const p = loader.loadAsync(`/animations/${id}/${id}.gltf`).then((gltf) => {
    const clip = gltf.animations[0];
    if (!clip) throw new Error(`No animation in ${id}.gltf`);
    clip.name = id;
    // Strip leading "Armature|" track-name prefix if present, so the clip's tracks
    // match the character skeleton bone names directly.
    for (const track of clip.tracks) {
      track.name = track.name.replace(/^.*\|/, "");
    }
    clip.optimize();
    return clip;
  });
  animCache.set(id, p);
  return p;
}

/** Eagerly fetch every animation in parallel so the first time playback never stalls. */
export function prefetchAllAnimations(): Promise<unknown> {
  return Promise.all(ANIMATIONS.map((a) => loadAnimation(a)));
}

/** Deterministic character pick from a player ID. */
export function pickCharacterForPlayer(playerId: string): CharacterId {
  let h = 0;
  for (let i = 0; i < playerId.length; i++) {
    h = (h * 31 + playerId.charCodeAt(i)) | 0;
  }
  const idx = ((h % CHARACTERS.length) + CHARACTERS.length) % CHARACTERS.length;
  return CHARACTERS[idx]!;
}
